import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import api from '@/services/Api';
import { successSwal, warningSwal } from '@/helper/swals';
import { RootState, UsersState, UserValidations } from '@/store/types';
import User, { UserRoleType } from '@/types/User';
import { DataResponse } from '@/types';

export type LoadUsersData = DataResponse<Partial<User>[], {
    roles: UserRoleType[];
    validations: UserValidations;
}>;

export type UsersFilter = {
    deaktiviert: string;
}

export type LoadUsersPayload = {
    filter: UsersFilter;
}

export type SetUsersDialogs = {
    edit?: boolean;
    selfEdit?: boolean;
}

export type SetUsersLoaders = {
    save?: boolean;
    delete?: boolean;
    users?: boolean;
}

type Context = ActionContext<UsersState, RootState>;

export const users: Module<UsersState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
            selfEdit: false,
        },
        loaders: {
            save: false,
            delete: false,
            users: false,
        },
        users: [],
        roles: [],
        validations: {
            username: { min: 1, max: 50 },
            password: { min: 1, max: 100 },
        },
    },
    actions: {
        loadUsers({ commit }: Context, payload: LoadUsersPayload): void {
            commit('setLoaders', { users: true });

            const data = {
                ...payload,
            };

            api.post('verwaltung/users/load', data)
                .then((response) => commit('setUsers', response.data))
                .catch(console.error)
                .finally(() => commit('setLoaders', { users: false }));
        },
        updateUser({ commit, dispatch, rootState }: Context,
                   { user, filter }: { user: User, filter: { deaktiviert: string } }): void {
            commit('setLoaders', { save: true });

            api.post('verwaltung/users/speichern', user)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        successSwal('Benutzer', 'gespeichert');
                        commit('setDialogs', { edit: false, selfEdit: false });

                        const user = response.data.body as User;
                        const authUser = rootState.authentication.user;

                        const isAdmin = authUser?.isAdmin ?? false;

                        if (user.user_id === authUser?.user_id) {
                            dispatch('authentication/updateUser', user, { root: true });
                            dispatch('authentication/loadPermissions', null, { root: true });
                        }

                        if (isAdmin || authUser?.isAdmin) {
                            dispatch('loadUsers', { filter });
                        }
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Fehler beim Speichern.');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { save: false }));
        },
        deleteUser({ commit, dispatch, rootState }: Context,
                   { user: { user_id }, filter }: { user: User, filter: { deaktiviert: string } }): void {
            commit('setLoaders', { delete: true });

            api.post('verwaltung/users/loeschen', { user_id })
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        const authUser = rootState.authentication.user;

                        successSwal('Benutzer', 'gelöscht');
                        commit('setDialogs', { edit: false });

                        if (authUser?.isAdmin) {
                            dispatch('loadUsers', { filter });
                        }
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Fehler beim Löschen.');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { delete: false }));
        },
    },
    mutations: {
        setUsers(state: UsersState, { body, selections }: LoadUsersData): void {
            state.users = body.map((data) => new User(data));
            state.roles = selections.roles;
            state.validations = selections.validations;
        },
        setDialogs(state: UsersState, dialogs: SetUsersDialogs): void {
            state.dialogs = {
                ...state.dialogs,
                ...dialogs,
            };
        },
        setLoaders(state: UsersState, loaders: SetUsersLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
    },
};
